import { createSlice } from '@reduxjs/toolkit';

import { TProductCategory } from '@lib/core/products/types';
import serviceInstanceSlice, { IServiceInstance } from '@lib/core/service/slices/serviceInstanceSlice';
import { ThirdPartyConnectModalScenario, ThirdPartyConnectModalShown } from '@lib/core/service/types/interface';
import { languages } from '@lib/tools/locale/utils/consts';
import { PRODUCT_CATEGORY_NONE } from '@lib/tools/shared/helpers/consts';

interface IService {
  isTastePathOnboardingShown: boolean;
  isSelectProductTooltipShown: boolean;
  isTasteIdGuideShown: boolean;
  isHostMocked: boolean;
  isDevToolsEnabled: boolean;
  isLocaleEditMode: boolean;
  isFooterTutorialDisplayed: boolean;
  isFirstTimeCatalogShowed: boolean;
  isFunctionalityExplanationDisplayed: boolean;
  productCategory: TProductCategory;
  shouldShowTastePathStartPage: boolean;
  shouldShowGamificationStartPage: boolean;
  shouldShowScanBarcode: boolean;
  shouldHideDownloadAppCard: boolean;
  isThirdPartyConnectModalShown: ThirdPartyConnectModalShown;
  fidelityCardIdFromKiosk: string;
  thirdPartyConnectModalOpenScenario?: {
    scenario: ThirdPartyConnectModalScenario | '';
    productName: string;
  };
  previousRoute: string;
  route: string;
  lang: string;
  serviceInstance: IServiceInstance;
  isTestResultView: boolean;
  isKioskSessionResetInProgress: boolean;
}

export const initialState: IService = {
  fidelityCardIdFromKiosk: '',
  isDevToolsEnabled: false,
  isFirstTimeCatalogShowed: false,
  isFooterTutorialDisplayed: false,
  isFunctionalityExplanationDisplayed: false,
  isHostMocked: false,
  isKioskSessionResetInProgress: false,
  isLocaleEditMode: false,
  isSelectProductTooltipShown: false,
  isTasteIdGuideShown: false,
  isTastePathOnboardingShown: false,
  isTestResultView: false,
  isThirdPartyConnectModalShown: {
    location: false,
    rate4: false,
    rate5: false,
    wishlist: false,
  },
  lang: languages.ENGLISH,
  previousRoute: '',
  productCategory: PRODUCT_CATEGORY_NONE,
  route: '',
  serviceInstance: serviceInstanceSlice(undefined, { type: '' }),
  shouldHideDownloadAppCard: false,
  shouldShowGamificationStartPage: true,
  shouldShowScanBarcode: false,
  shouldShowTastePathStartPage: true,
  thirdPartyConnectModalOpenScenario: {
    productName: '',
    scenario: '',
  },
};

export const serviceSlice = createSlice({
  extraReducers: builder => {
    builder.addDefaultCase((state, action) => {
      state.serviceInstance = serviceInstanceSlice(state.serviceInstance, action);
    });
  },
  initialState,
  name: 'service',
  reducers: {
    setFidelityCardIdFromKiosk: (state, action) => {
      state.fidelityCardIdFromKiosk = action.payload;
    },
    setHideGamificationStartPage: state => {
      state.shouldShowGamificationStartPage = false;
    },
    setIsDevToolsDisabled(state) {
      state.isDevToolsEnabled = false;
    },
    setIsDevToolsEnabled(state) {
      state.isDevToolsEnabled = true;
    },
    setIsFirstTimeCatalogShowed(state) {
      state.isFirstTimeCatalogShowed = true;
    },
    setIsFooterTutorialDisplayed: (state, action) => {
      state.isFooterTutorialDisplayed = action.payload;
    },
    setIsFunctionalityExplanationDisplayed: (state, action) => {
      state.isFunctionalityExplanationDisplayed = action.payload;
    },
    setIsKioskSessionResetInProgress(state, { payload }: { payload: boolean }) {
      state.isKioskSessionResetInProgress = payload;
    },
    setIsTasteIdGuideShown: (state, action) => {
      state.isTasteIdGuideShown = action.payload;
    },
    setIsTastePathOnboardingShown(state, { payload }) {
      state.isTastePathOnboardingShown = payload;
    },
    setIsThirdPartyConnectShown: (state, action) => {
      return {
        ...state,
        isThirdPartyConnectModalShown: {
          ...state.isThirdPartyConnectModalShown,
          [action.payload]: true,
        },
      };
    },
    setProductCategory(state, action) {
      state.productCategory = action.payload;
    },
    setSelectProductTooltipShown: state => {
      state.isSelectProductTooltipShown = true;
    },
    setServiceLocale(state, action) {
      state.lang = action.payload;
    },
    setShouldHideDownloadAppCard: state => {
      state.shouldHideDownloadAppCard = true;
    },
    setShowTastePathStartPage(state, action) {
      state.shouldShowTastePathStartPage = action.payload;
    },
    setTestResultView(state, { payload }: { payload: boolean }) {
      state.isTestResultView = payload;
    },
    setThirdPartyConnectModalOpenScenario: (state, action) => {
      state.thirdPartyConnectModalOpenScenario = {
        productName: action.payload.productName,
        scenario: action.payload.scenario,
      };
    },
    toggleLocaleEditMode(state, action) {
      state.isLocaleEditMode = action.payload;
    },
    toggleMockHost(state, action) {
      state.isHostMocked = action.payload;
    },
    toggleShowScanBarcode(state, action) {
      state.shouldShowScanBarcode = action.payload;
    },
    writeHistory(state, action) {
      if (state.route !== action.payload) {
        if (!state.previousRoute) {
          return {
            ...state,
            previousRoute: action.payload,
            route: action.payload,
          };
        }
        return {
          ...state,
          previousRoute: state.route,
          route: action.payload,
        };
      }
      return {
        ...state,
      };
    },
  },
});

export default serviceSlice.reducer;

export const {
  setServiceLocale,
  setIsDevToolsEnabled,
  setIsDevToolsDisabled,
  setProductCategory,
  setShowTastePathStartPage,
  setIsTastePathOnboardingShown,
  writeHistory,
  toggleShowScanBarcode,
  toggleLocaleEditMode,
  toggleMockHost,
  setIsTasteIdGuideShown,
  setShouldHideDownloadAppCard,
  setThirdPartyConnectModalOpenScenario,
  setIsThirdPartyConnectShown,
  setFidelityCardIdFromKiosk,
  setIsFooterTutorialDisplayed,
  setIsFirstTimeCatalogShowed,
  setIsFunctionalityExplanationDisplayed,
  setTestResultView,
  setIsKioskSessionResetInProgress,
  setSelectProductTooltipShown,
  setHideGamificationStartPage,
} = serviceSlice.actions;
